import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
} from '@rocco/components/drawer';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { atom, useAtom } from 'jotai';
import { getPlatformInfo } from '../utils/platform';
import type { Platform } from '../utils/platform';

/**
 * Atom for managing the visibility state of the social sharing modal.
 * Used to control whether the modal is currently displayed to the user.
 */
export const uiStateAtomSocialSharingModalIsOpened = atom(false);

/**
 * Options for sharing content on social platforms.
 * Contains the essential information needed for sharing across different platforms.
 */
interface SocialSharingModalOpts {
    /** The URL to be shared (typically the current page URL) */
    url: string;

    /** Optional title of the content being shared */
    title?: string;

    /** Optional description of the content being shared */
    description?: string;
}

/**
 * Props interface for the SocialSharingModal component.
 * Defines the configuration and callbacks needed for the social sharing functionality.
 */
interface SocialSharingModalProps {
    /**
     * Display mode that determines how the modal is presented
     * (mobile optimizes for touch, desktop for mouse interactions)
     */
    displayMode: DisplayMode;

    /**
     * Available social platforms to display as sharing options
     * Each platform will be rendered as a clickable icon
     */
    platforms: Platform[];

    /**
     * Callback fired when a user selects a platform to share on.
     * Provides both the selected platform and sharing options (URL, title, etc.)
     *
     * @param platform - The selected platform (e.g., 'facebook', 'twitter')
     * @param opts - Sharing options containing URL and metadata
     */
    onShare: (platform: Platform, opts?: SocialSharingModalOpts) => void;

    /**
     * Optional callback fired when the modal is closed without sharing.
     * Useful for tracking abandoned sharing attempts or cleaning up state.
     */
    onCancel?: () => void;
}

/**
 * Modal component for sharing content on social media platforms.
 *
 * This component:
 * 1. Displays a drawer/modal with a list of available social platforms
 * 2. Adapts its appearance based on the current display mode (mobile/desktop)
 * 3. Handles platform selection and provides sharing metadata to the parent
 * 4. Manages its own open/closed state via a Jotai atom
 *
 * The modal automatically collects the current page URL and title when a
 * platform is selected, passing this information to the onShare callback.
 *
 * @example
 * // Basic usage:
 * <SocialSharingModal
 *   displayMode={DisplayMode.Desktop}
 *   platforms={['facebook', 'twitter', 'linkedin', 'email']}
 *   onShare={(platform, opts) => {
 *     console.log(`Sharing on ${platform}: ${opts?.url}`);
 *     // Handle the actual sharing logic
 *   }}
 *   onCancel={() => console.log('Sharing cancelled')}
 * />
 *
 * // To open the modal from elsewhere in your app:
 * const [_, setIsOpen] = useAtom(uiStateAtomSocialSharingModalIsOpened);
 * const handleOpenShareModal = () => setIsOpen(true);
 *
 * @param {SocialSharingModalProps} props - Component props
 * @returns {React.JSX.Element} Rendered component
 */
export const SocialSharingModal = (props: SocialSharingModalProps) => {
    const { displayMode, platforms, onShare, onCancel } = props;

    const [isOpen, setIsOpen] = useAtom(uiStateAtomSocialSharingModalIsOpened);

    const isMobile = displayMode === DisplayMode.Mobile;

    /**
     * Handles platform selection by gathering page information and calling the onShare callback.
     * Automatically collects the current page URL and title for sharing.
     *
     * @param platform - The selected social platform
     */
    const handlePlatformSelect = (platform: Platform) => {
        const pageUrl = window.location.href;
        const pageTitle = document.title;

        onShare(platform, {
            url: pageUrl,
            title: pageTitle,
        });
    };

    return (
        <Drawer
            handleOnly={!isMobile}
            open={isOpen}
            onOpenChange={setIsOpen}
            onClose={() => {
                setIsOpen(false);
                onCancel?.();
            }}
        >
            <DrawerContent
                variant="glassy"
                overlayVariant="dark"
                noHandle={!isMobile}
                hideDefaultCloseButton={isMobile}
                limitMaxHeight={isMobile}
            >
                <div className="flex h-full w-full flex-col md:w-224.5">
                    <DrawerHeader variant="border" alignment="left">
                        <DrawerTitle>Share</DrawerTitle>
                        {/* Suppress missing description console warning */}
                        <DrawerDescription className="hidden" aria-hidden />
                    </DrawerHeader>

                    <div className="h-[20rem] scrollbar-hidden w-dvw shrink overflow-y-scroll md:h-auto md:w-full md:px-6">
                        <div className="px-6 py-4 md:px-16 md:py-9">
                            <div className="flex flex-row justify-center gap-16">
                                {platforms.map((platform) => {
                                    const { icon, displayName } =
                                        getPlatformInfo(platform);

                                    return (
                                        <div
                                            key={platform}
                                            className="group flex cursor-pointer flex-col items-center justify-center gap-3.5"
                                            onClick={() =>
                                                handlePlatformSelect(platform)
                                            }
                                            aria-label={`Share this page on ${displayName}`}
                                        >
                                            <div className="flex size-16 items-center justify-center rounded-[var(--radius-btn-m)] bg-gray-100 group-hover:bg-brick-500">
                                                {icon}
                                            </div>
                                            <span className="text-sm font-medium whitespace-nowrap">
                                                {displayName}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
};
